<script setup lang="ts">
import {
  ref,
  computed,
  onUnmounted,
} from 'vue';

import { useI18n } from 'vue-i18n';
import {
  LpDropdown,
  LpCard,
  LpMarkerBtn,
  LpModal,
} from '@thehotelsnetwork/lampo';
import moment from 'moment';
import { capitalize } from 'lodash-es';

import BmCalendar from './Calendar.vue';

import useFilterState from '@/state/useFilterState';
import useUserState from '@/state/useUserState';

import useTruncate from '@/utils/useTruncate';

import { track } from '@/utils/analytics';
import navigate from '@/utils/navigate';
import { premiumLandingUrl } from '@/config/redirectUrls';

const {
  setApplyCtpStayDates,
} = useFilterState();

interface DateOption {
  tag: string;
  label: string;
  sublabel?: string;
  premium?: boolean;
}

interface DatesConfig {
  tagSelected?: string;
  defaultLabel?: string;
  defaultOptions?: DateOption[];
  onDatesSelect?: (data: { db: number; de: number; tag: string; label: string }) => void;
  onDatesClear?: () => void;
  customDateRangeDisabled?: boolean;
  clearButton?: boolean;
  calendarConfig?: object;
}

const props = withDefaults(defineProps<{
  disabled?: boolean;
  dB?: number;
  dE?: number;
  minMonth?: number;
  maxMonth?: number;
  config: DatesConfig;
}>(), {
  disabled: false,
  dB: 0,
  dE: 0,
  minMonth: 0,
  config: () => ({}),
  maxMonth: undefined,
});

const { t } = useI18n();
const upsellingText = t('Don\'t miss a single detail. Go Pro to view unlimited dates.');

// Refs
const showList = ref(true);
const showCalendar = ref(false);
const headerLabel = ref(null);

const { isTruncate, stop } = useTruncate(headerLabel);
const { showUpselling } = useUserState();

const isActive = computed(() => !!(props.config.tagSelected));

// Computed
const datesLabel = computed(() => {
  let res = t(props.config.defaultLabel || 'Dates Selector');
  if (props.config.tagSelected === 'custom'
        && props.dB && props.dE) {
    const startMoment = moment.unix(props.dB);
    const endMoment = moment.unix(props.dE);

    if (startMoment.isSame(endMoment, 'day')) {
      res = `${capitalize(startMoment.format('MMM D, YYYY'))}`;
    } else {
      res = `${capitalize(startMoment.format('MMM D, YYYY'))} - ${capitalize(endMoment.format('MMM D, YYYY'))}`;
    }
  } else if (props.config && props.config.defaultOptions) {
    props.config.defaultOptions.forEach((opt) => {
      if (opt.tag === props.config.tagSelected) {
        res = t(opt.label);
      }
    });
  }
  return res;
});

const tpConfig = {
  type: 'info',
  content: { text: datesLabel },
  preferred: ['top'],
  show: isTruncate,
};

const toggleCalendar = () => {
  showCalendar.value = !showCalendar.value;
};

const onSelectDateRange = (close: () => void) => (dB: number, dE: number) => {
  if (dB && dE) {
    close();
    showList.value = true;

    // Format dates: start / end of both days
    const formatedStart = moment.unix(dB).startOf('day').unix();
    const formatedEnd = moment.unix(dE).endOf('day').unix();

    // Clear toggle
    setApplyCtpStayDates(false);

    // Update dates
    props.config.onDatesSelect?.({
      db: formatedStart,
      de: formatedEnd,
      tag: 'custom',
      label: 'Custom',
    });
  }
};

const onClearSelection = () => {
  if (props.config
        && props.config.onDatesClear
        && typeof props.config.onDatesClear === 'function') {
    props.config.onDatesClear();
  }
};

const selectPredefinedOption = (option: DateOption) => {
  if (!option.premium) {
    if (typeof props.config.onDatesSelect === 'function') {
      props.config.onDatesSelect({
        db: 0,
        de: 0,
        tag: option.tag,
        label: option.label,
      });
    }
  }
};

const handleUpgradeClick = () => {
  track('premium - click - geolocation');

  navigate(premiumLandingUrl, { external: true, newTab: true });
};

onUnmounted(stop);

</script>

<template>
  <div class="relative z-10 dates-selector dropdown">
    <LpDropdown
      :disabled="disabled"
    >
      <template #btn>
        <div
          data-testid="datesDropdownHeader"
          :class="[
            'dropdown-header rounded-full',
            isActive ? 'active' : 'inactive',
            { disabled: disabled },
          ]"
        >
          <div
            ref="headerLabel"
            v-tooltip="tpConfig"
            class="truncate"
          >
            {{ datesLabel }}
          </div>
        </div>
      </template>

      <template #menu="{ closeMenu }">
        <LpCard
          rounded-size="big"
          class="p-2 font-medium date-selector-wrapper text-base-900"
        >
          <div
            v-for="option in config.defaultOptions"
            :key="option.label"
            class="mb-1"
            :test-id="option.tag"
            @click="() => {
              selectPredefinedOption(option);
              !option.premium && closeMenu();
            }"
            @keydown="() => {
              selectPredefinedOption(option);
              !option.premium && closeMenu();
            }"
          >
            <LpTooltip
              v-if="option.premium"
              size="small"
              placement="right"
              :disabled="!showUpselling"
              :max-width="221"
              :min-width="221"
              nowrap="false"
              custom-wrapper
              mode="click"
            >
              <template #trigger>
                <LpMarkerBtn
                  :active="option.tag === config.tagSelected"
                  :ui="{
                    disabled: 'text-base-400',
                  }"
                  class="disabled"
                >
                  <div>
                    {{ t(option.label) }}
                  </div>
                  <div
                    v-if="option.sublabel"
                    class="text-sm font-light text-base-700"
                    test-id="sublabel"
                  >
                    {{ option.sublabel }}
                  </div>
                </LpMarkerBtn>
              </template>
              <template
                #tooltip
              >
                <LpCard class="p-2 bg-grape-100 text-grape-500">
                  <p
                    class="upselling"
                    v-html="upsellingText"
                  />
                  <a
                    class="underline !font-bold cursor-pointer text-sm text-unicorn-500"
                    @click="() => {
                      closeMenu();
                      handleUpgradeClick();
                    }"
                    @keydown="() => {
                      closeMenu();
                      handleUpgradeClick();
                    }"
                  >
                    {{ t('Upgrade now') }}
                  </a>
                </LpCard>
              </template>
            </LpTooltip>
            <LpMarkerBtn
              v-else
              :active="option.tag === config.tagSelected"
              :ui="{
                disabled: 'text-base-400',
              }"
            >
              <div>
                {{ t(option.label) }}
              </div>
              <div
                v-if="option.sublabel"
                class="text-sm font-light text-base-700"
                test-id="sublabel"
              >
                {{ option.sublabel }}
              </div>
            </LpMarkerBtn>
          </div>

          <slot
            name="additionalOptions"
            :trigger-dates="selectPredefinedOption"
          />

          <template v-if="!config.customDateRangeDisabled">
            <LpDivider class="m-2" />

            <div
              data-testid="dateRangeBtn"
              data-calendar="true"
              @click="() => {
                toggleCalendar();
                closeMenu();
              }"
              @keydown="() => {
                toggleCalendar();
                closeMenu();
              }"
            >
              <LpMarkerBtn
                :active="config.tagSelected === 'custom'"
                :ui="{
                  disabled: 'text-base-400',
                }"
              >
                {{ `${t('Select a date range')}...` }}
              </LpMarkerBtn>
            </div>
          </template>

          <template v-if="config.clearButton">
            <LpDivider class="m-2" />

            <div
              class="font-medium"
              @click="() => {
                onClearSelection();
                closeMenu();
              }"
              @keydown="() => {
                onClearSelection();
                closeMenu();
              }"
            >
              <LpMarkerBtn
                :ui="{
                  disabled: 'text-base-400',
                }"
              >
                {{ t('Clear selection') }}
              </LpMarkerBtn>
            </div>
          </template>
        </LpCard>
      </template>
    </LpDropdown>
    <LpModal
      v-model="showCalendar"
      content-classes="max-w-3xl"
    >
      <template #content="{ close }">
        <bm-calendar
          :d-b="dB"
          :d-e="dE"
          :min-month="minMonth"
          :max-month="maxMonth"
          :on-cancel-click="close"
          :on-select-date-range="onSelectDateRange(close)"
          :config="config.calendarConfig"
        />
      </template>
    </LpModal>
  </div>
</template>

<style lang="postcss" scoped>
.date-selector-wrapper {
  min-width: 14rem;
}
.upselling {
  @apply text-sm ;
  :deep(strong) {
    @apply font-semibold cursor-pointer;
    &:hover {
      @apply underline;
    }
  }
}
</style>
